<template>
    <grid-item
        ref="gridItem"
        :x="tabWidget.x"
        :y="tabWidget.y > 0 ? tabWidget.y : 0"
        :w="tabWidget.w"
        :h="tabWidget.h"
        :i="tabWidget.i.toString()"
        :static="tabWidget.static"
        :is-resizable="canResize && isLargeScreen"
        :min-h="minH"
        :min-w="minW"
        :drag-allow-from="`.${faIconDragAndDropClassName}`"
        @resized="resized"
    >
        <b-card
            ref="card"
            :class="[...cardClass, isHeightResizable ? 'min-height-100' : 'h-100 min-height-100']"
            no-body
        >
            <b-card-header
                v-show="showHeader"
                :class="[faIconDragAndDropClassName, headerStyleClassName]"
                @mousedown="handleHeaderMouseDown"
                @mouseup="handleHeaderMouseUp"
            >
                <slot name="card-title-prepend"></slot>
                <b-card-title class="mr-auto">
                    <slot name="card-title"></slot>
                </b-card-title>
                <fa-icon-drag-and-drop v-if="canDragAndDrop" class="pr-2" />

                <button v-if="excelHref" class="btn btn-sm text-secondary p-0 mr-2" @click="exportToExcel">
                    <font-awesome-icon :icon="['fas', 'file-excel']"></font-awesome-icon>
                </button>

                <button
                    v-if="canUpdate"
                    class="btn btn-sm text-secondary p-0 mr-2"
                    @click="handleTabWidgetSettingsClick"
                >
                    <font-awesome-icon :icon="['fas', 'gears']"></font-awesome-icon>
                </button>
                <button v-if="canDelete" class="btn btn-sm text-secondary p-0" @click="handleDeleteTabWidgetClick">
                    <font-awesome-icon :icon="['fas', 'trash']"></font-awesome-icon>
                </button>
            </b-card-header>
            <b-card-body :class="[...cardBodyClass, 'h-100']">
                <slot name="card-body"></slot>
            </b-card-body>
        </b-card>
        <modal :id="removeTabWidgetModalID" title="Remove Widget" @ok="deleteTabWidget"
            >Are you sure you want to remove this widget?</modal
        >
        <slot name="tab-widget-settings-modal"></slot>
    </grid-item>
</template>

<script>
import ResizeSensor from 'css-element-queries/src/ResizeSensor';
import { GridItem } from 'grid-layout-plus';
import debounce from 'lodash/debounce';
import API from '../../../../api';
import CardGrid from '../../../generic/card-grid/card-grid.vue';
import { faIconDragAndDropClassName } from '../../../generic/fa-icon-drag-and-drop/constants';
import FaIconDragAndDrop from '../../../generic/fa-icon-drag-and-drop/fa-icon-drag-and-drop.vue';
import Modal from '../../../generic/modal/modal.vue';
import { formatResponseError } from '../../../../utils/api';
import { isLargeScreen } from '../../../../utils/screens';
import { headerStyleClasses } from '../helpers';

export default {
    name: 'CardGridItem',
    components: {
        CardGrid,
        FaIconDragAndDrop,
        GridItem: GridItem,
        Modal,
    },
    props: {
        tabWidget: Object,
        cardClass: {
            type: Array,
            default: () => [],
        },
        cardBodyClass: {
            type: Array,
            default: () => [],
        },
        minH: {
            type: Number,
            default: 1,
        },
        minW: {
            type: Number,
            default: 1,
        },
        resized: {
            type: Function,
            default: () => {},
        },
        isHeightResizable: {
            type: Boolean,
            default: false,
        },
        canDelete: {
            type: Boolean,
            default: true,
        },
        canDragAndDrop: {
            type: Boolean,
            default: true,
        },
        canResize: {
            type: Boolean,
            default: true,
        },
        canUpdate: {
            type: Boolean,
            default: true,
        },
        showHeader: {
            type: Boolean,
            default: true,
        },
        excelHref: {
            type: String,
            default: '',
        },
    },
    computed: {
        isLargeScreen() {
            return isLargeScreen();
        },
        headerStyleClassName() {
            const { id } = this.tabWidget;
            const headerStyleIndex = id % 8;
            return headerStyleClasses[headerStyleIndex];
        },
    },
    inject: ['resizeEmitEvent'],
    data() {
        return {
            faIconDragAndDropClassName,
            resizeHandleRef: null,
            previousCardHeight: null,
            removeTabWidgetModalID: `remove-tab-widget-modal-id-${this.tabWidget.id}`,
        };
    },
    mounted() {
        this.addResizeHandleEventListeners();

        if (this.isHeightResizable) {
            this.addResizeSensor();
        }
    },
    beforeUnmount() {
        this.removeResizeHandleEventListeners();
    },
    methods: {
        handleHeaderMouseUp() {
            this.$store.commit('dashboard/setIsDraggingWidget', false);
        },
        handleHeaderMouseDown() {
            this.$store.commit('dashboard/setIsDraggingWidget', true);
        },
        handleResizeHandleMouseUp() {
            this.$store.commit('dashboard/setIsResizingWidget', false);
        },
        handleResizeHandleMouseDown() {
            this.$store.commit('dashboard/setIsResizingWidget', true);
        },
        handleDeleteTabWidgetClick() {
            this.$bvModal.show(this.removeTabWidgetModalID);
        },
        handleTabWidgetSettingsClick() {
            this.$emit('tab-widget-settings-click');
        },
        handleDownloadClick() {
            this.$emit('download-click');
        },
        addResizeSensor() {
            new ResizeSensor(
                this.$refs.card,
                debounce(() => {
                    const gridItem = this.$refs.gridItem;
                    const card = this.$refs.card;
                    const cardHeight = card && card.clientHeight;
                    const previousCardHeight = this.previousCardHeight;
                    const margin = gridItem.state.margin[1];
                    const { i, x, y, w } = gridItem.$on('');
                    if (gridItem && card && cardHeight !== previousCardHeight) {
                        const borderSize = 2;
                        const gridItemH = (cardHeight + margin + borderSize) / (gridItem.state.rowHeight + margin);
                        this.resizeEmitEvent('resizeend', i, x, y, gridItemH, w);

                        this.previousCardHeight = cardHeight;
                    }
                }, 250)
            );
        },
        addResizeHandleEventListeners(attempt = 1) {
            const attemptsLimit = 5;
            if (attempt > attemptsLimit) {
                return;
            }

            this.$nextTick(() => {
                this.resizeHandleRef = this.$refs.gridItem?.$refs.handle;
                if (this.resizeHandleRef) {
                    this.resizeHandleRef.addEventListener('mousedown', this.handleResizeHandleMouseDown);
                    this.resizeHandleRef.addEventListener('mouseup', this.handleResizeHandleMouseUp);
                } else {
                    this.addResizeHandleEventListeners(attempt + 1);
                }
            });
        },
        removeResizeHandleEventListeners() {
            if (this.resizeHandleRef) {
                this.resizeHandleRef.removeEventListener('mousedown', this.handleResizeHandleMouseDown);
                this.resizeHandleRef.removeEventListener('mouseup', this.handleResizeHandleMouseUp);
            }
        },
        async deleteTabWidget() {
            try {
                await this.$store.dispatch('dashboard/deleteDashboardTabWidget', {
                    id: this.tabWidget.id,
                    tab_id: this.tabWidget.tab_id,
                });
            } catch (err) {
                const { generic } = formatResponseError(err);
                this.errorToast({ text: generic || 'Could not delete widget.' });
            }
        },
        async exportToExcel() {
            this.generatingExcelReportSuccessToast();

            this.busy = true;
            this.$emit('busy', true);
            try {
                const response = await API.get(this.excelHref, { responseType: 'blob' });
                saveAs(response.data, this.getFilenameFromResponse(response));
            } catch (err) {
                const text = !!err.response && !!err.response.data ? await err.response.data.text() : null;
                if (text && text.includes('Too many records')) {
                    if (this.onTooManyRecords) {
                        this.onTooManyRecords();
                    } else {
                        this.errorToast({ text: "Couldn't generate Excel because of too many records." });
                    }
                } else {
                    this.errorToast({ text: "Couldn't download Excel." });
                }
            } finally {
                this.busy = false;
                this.$emit('busy', false);
            }
        },
        getFilenameFromResponse(response) {
            const contentDisposition = response.headers['content-disposition'];
            if (contentDisposition) {
                return /filename="(?<filename>.*)"/.exec(contentDisposition)?.groups?.filename;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.min-height-100 {
    min-height: 100%;
}
:deep(.chart-widget-spinner) {
    left: 45%;
    top: 40%;
    width: auto;
    height: auto;
    position: absolute;
}
</style>
